import type React from "react";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import Pusher from "pusher-js";
import { pusherConfig } from "../pusherConfig";

interface PusherContextValue {
	client: Pusher | null;
}

const PusherContext = createContext<PusherContextValue>({ client: null });

export const usePusher = () => {
	const context = useContext(PusherContext);
	if (!context.client) {
		throw new Error("usePusher must be used within PusherProvider");
	}
	return context.client;
};

interface PusherProviderProps {
	children: React.ReactNode;
	value?: PusherContextValue;
}

export const PusherProvider: React.FC<PusherProviderProps> = ({
	children,
	value,
}) => {
	const config = pusherConfig();
	const clientRef = useRef<Pusher | null>(null);

	// Use provided value (for testing) or create new client
	const contextValue = useMemo<PusherContextValue>(() => {
		if (value) {
			return value;
		}

		if (!clientRef.current) {
			clientRef.current = new Pusher(config.client.clientKey, {
				cluster: config.client.cluster,
				authEndpoint: config.client.authEndpoint,
				auth: {
					params: config.client.auth.params,
					headers: config.client.auth.headers,
				},
			});
		}

		return { client: clientRef.current };
	}, [value, config]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (clientRef.current && !value) {
				clientRef.current.disconnect();
				clientRef.current = null;
			}
		};
	}, [value]);

	return (
		<PusherContext.Provider value={contextValue}>
			{children}
		</PusherContext.Provider>
	);
};
